import { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { Survey } from "survey-react-ui";
import { Model, settings } from "survey-core";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { isAgeValid } from "../../utils/dateValidation.js";
import { __API_URL__ } from "../../SERVER_URL";
import { INV_RELATED_ROLES, USER_ROLES } from "../../utils/constants.js";
import logUserAction from "../../apis/userLogger.js";
import useGetVisitSchedule from "../../hooks/useGetVisitSchedule.js";
import useGetPreviousEcrfsVisits from "../../hooks/useGetPreviousEcrfsVisits";
import CustomButton from "../UI/CustomButton.jsx";
import useUserActionsLogsMutation from "../../hooks/useUserActionsLogsMutation.js";

/*
  These two functions were created to deal with age field issue in Vanda Pharma 2.
  We had to enforce ddmmyyyy date format which was causing problem with built
  in age() function of surveyjs which expects yyyymmdd, saveMaskedValue: true
  was forcing the date to be ddmmyyyy even in age() function.
  Using these two functions to convert to ddmmyyyy manually on saving and to
  convert to yyyymmdd on loading so surveyjs can read it.

  For this to work:
    date of birth field must be named BRTHDATE
    saveMaskedValue of date of birth field must be false
*/
function convertToDDMMYYYY(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return "";

  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";

  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

function convertToYYYYMMDD(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return "";

  const parts = dateStr.split("/");
  if (parts.length !== 3) return "";

  const [day, month, year] = parts;
  return `${year}-${month}-${day}`;
}

function EcrfBlocks({
  visit_number,
  visitName,
  visitId,
  setBlockId,
  selectedBlock,
  photo_session,
  fromQueryTable,
  showEcrfBlock,
  setShowEcrfBlock,
  isOpen,
  patientId,
  trialId,
  trialName,
  selectedSessionId,
  photoSessionId,
  allowCopyingFromOtherVisitsEcrfs,
  setHasUnsavedEcrfChanges,
  show_incomplete_date_warning_on_ecrf = false,
  isTrialLocked = false,
  useCompactModal = false,
}) {
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const queryClient = useQueryClient();
  const logAction = useUserActionsLogsMutation();

  const [surveyId, setSurveyId] = useState(0);
  const [survey, setSurvey] = useState(new Model({}));
  const [answerJson, setAnswerJson] = useState({});
  const [largeModalShow, setLargeModalShow] = useState(false);
  const [statusObject, setStatusObject] = useState({});
  const isShowingErrorsOnly = useRef(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  const [showDateWarning, setShowDateWarning] = useState(false);

  const [selectedVisitOption, setSelectedVisitOption] = useState("");

  const { data: visits = [] } = useGetPreviousEcrfsVisits({
    trialId: trialId,
    patientId: patientId,
    ecrfModelId: surveyId,
    enabled: !!trialId && !!patientId && !!surveyId && allowCopyingFromOtherVisitsEcrfs,
  });

  const { data: visitScheduleData } = useGetVisitSchedule({
    visitId: visitId,
    patientId: patientId,
    enabled: !!visitId && patientId,
  });

  const visitScheduleDataRef = useRef(visitScheduleData);
  useEffect(() => {
    visitScheduleDataRef.current = visitScheduleData;
  }, [visitScheduleData]);

  useEffect(() => {
    if (
      role === USER_ROLES.INVESTIGATOR ||
      role === USER_ROLES.SUB_INVESTIGATOR ||
      role === USER_ROLES.CRC ||
      role === USER_ROLES.SUPER_ADMIN ||
      role === USER_ROLES.REVIEWER
    ) {
      setShowResetButton(true);
    } else {
      setShowResetButton(false);
    }
  }, [role]);

  useEffect(() => {
    const surveyAnswerJson = { ...answerJson };

    // While loading convert to yyyymmdd if BRTHDATE field exists
    if ("BRTHDATE" in surveyAnswerJson && surveyAnswerJson.BRTHDATE) {
      surveyAnswerJson.BRTHDATE = convertToYYYYMMDD(surveyAnswerJson.BRTHDATE);
    }

    survey.data = surveyAnswerJson;

    if (answerJson && isShowingErrorsOnly.current) {
      survey.validateCurrentPage();
    }
  }, [answerJson]);

  useEffect(() => {
    if (survey) {
      survey.runConditions();
      survey.runExpressions();

      survey.onAfterRenderQuestion.add((sender, options) => {
        if (!!options.question.inputType && options.question.inputType === "number") {
          const numericInput = options.htmlElement.getElementsByTagName("input")[0];
          numericInput.addEventListener("wheel", function (event) {
            numericInput.blur();
          });
        }
      });

      survey.onValueChanged.add(function (sender, options) {
        setHasUnsavedEcrfChanges?.(true);

        if (options.name === "HIGHT" || options.name === "WEIGHT") {
          const height = sender.getValue("HIGHT");
          const weight = sender.getValue("WEIGHT");

          if (height && weight && height > 0) {
            const bmi = Math.round((weight / Math.pow(height / 100, 2)) * 10) / 10;
            sender.setValue("BMI", bmi);
          }
        }
      });

      survey.onValidatedErrorsOnCurrentPage.add((x, options) => {
        if (isShowingErrorsOnly.current) {
          isShowingErrorsOnly.current = false;
          return;
        }

        const isFinalPage = x.isLastPage;

        const newJson = {};
        Object.assign(newJson, x.data);

        let hasErrors = false;
        let constraintError = false;
        let hasEmptyField = false;

        options.questions.forEach((q) => {
          if (
            q.value > q.jsonObj.max ||
            q.value < q.jsonObj.min ||
            (typeof q.value === "string" && !isAgeValid(q.value, 18, 75))
          ) {
            constraintError = true;
          }

          if (!q.value || q.jsonObj.type === "checkbox") hasEmptyField = true;

          hasErrors = true;
          newJson[q.name] = q.value;
        });

        let possibleIncorrectData =
          checkForInclusionExclusion(x).length > 0 || CheckDynamicValue(x).length > 0;

        if (patientId && hasErrors) {
          if (
            isFinalPage &&
            visitScheduleDataRef.current?.has_inclinic_activity &&
            !visitScheduleDataRef.current.inclinic_start_date &&
            (visit_number === -3 || visit_number >= 0)
          ) {
            setShowDateWarning(true);
          }

          saveSurveyAnswer(
            newJson,
            hasEmptyField || possibleIncorrectData,
            constraintError,
            isFinalPage
          );

          const message = hasEmptyField
            ? "Survey saved successfully with empty fields."
            : constraintError
              ? "Survey saved successfully with some out of bound values."
              : "Survey saved successfully with validation errors.";

          if (isFinalPage) setLargeModalShow(false);
          toast.info(message);
        } else if (patientId) {
          if (
            isFinalPage &&
            visitScheduleDataRef.current?.has_inclinic_activity &&
            (!visitScheduleDataRef.current.inclinic_start_date ||
              !visitScheduleDataRef.current.inclinic_completed_date)
          ) {
            setShowDateWarning(true);
          }
          saveSurveyAnswer(newJson, possibleIncorrectData, constraintError, isFinalPage);
          if (isFinalPage) {
            setLargeModalShow(false);
            toast.success("Survey saved successfully.");
          }
        }
      });

      survey.showCompletedPage = false;

      survey.onComplete.add(function (result) {
        if (!patientId) {
          survey.clear();
          survey.data = {};
        }
      });

      if (isTrialLocked) {
        survey.mode = "display";
      } else if (["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role) && patientId) {
        survey.mode = "display";
      } else if (isOpen === false) {
        survey.mode = "display";
      }

      // Render HTML in texts (e.g. <br> in labels); no markdown parsing, so titles like "1. Photo Review" stay plain text
      survey.onTextMarkdown.add(function (survey, options) {
        if (/<[a-z][\s\S]*>/i.test(options.text)) {
          options.html = options.text;
        }
      });

      const checkForInclusionExclusion = (result) => {
        let noAnswers = [];
        if (
          [
            "INCLUSION CRITERIA - Screening Visit (Visit 1)",
            "EXCLUSION CRITERIA - Screening Visit (Visit 1)",
          ].includes(result.title)
        ) {
          for (const [key, value] of Object.entries(result.data)) {
            if (result.title === "EXCLUSION CRITERIA - Screening Visit (Visit 1)") {
              if (value === true) {
                noAnswers.push(key);
              }
            } else {
              if (value === false) {
                noAnswers.push(key);
              }
            }
          }
          if (noAnswers.length > 0) {
            toast.info("Please exclude this patient");
          }
        }
        return noAnswers;
      };

      const CheckDynamicValue = (result) => {
        let noAnswers = [];
        if (["INCLUSION CRITERIA", "EXCLUSION CRITERIA"].includes(result.title)) {
          for (const [key, value] of Object.entries(result.data)) {
            if (Array.isArray(value)) {
              value.forEach((val) => {
                if (result.title === "EXCLUSION CRITERIA" && val === "Yes") {
                  noAnswers.push(key);
                } else if (result.title === "INCLUSION CRITERIA" && val === "No") {
                  noAnswers.push(key);
                }
              });
            }
          }
          if (noAnswers.length > 0) {
            toast.info("Please exclude this patient");
          }
        }
        return noAnswers;
      };

      return () => {
        setHasUnsavedEcrfChanges?.(false);
      };
    }
  }, [survey]);

  useEffect(() => {
    getSurvey();
  }, [selectedBlock]);

  useEffect(() => {
    if (surveyId !== 0 && patientId) getSurveyAnswer();
  }, [surveyId, patientId]);

  useEffect(() => {
    if (statusObject?.cro_block_status_bool === true || statusObject?.dm_freeze_bool === true) {
      survey.mode = "display";
    }
  }, [statusObject]);

  const getSurvey = async () => {
    // Enable validation on read-only fields
    settings.readOnly.enableValidation = true;

    try {
      // Send GET request to fetch the survey data for the selected block
      const response = await axios.get(
        `${__API_URL__}/api/ecrf/get_new_ecrf/?surveyName=${selectedBlock}`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      const surveyData = response?.data?.data;

      if (!surveyData) {
        console.warn("No survey data received.");
        return;
      }

      // Update survey JSON and ID in state
      setSurveyId(surveyData.id);

      // Optionally set block ID if the setter is available
      if (typeof setBlockId === "function") {
        setBlockId(surveyData.id);
      }

      // Create a new survey model from JSON
      const localSurvey = new Model(surveyData.surveyJson);

      // Determine if the survey should be read-only
      let isReadOnly = false;

      if (role === USER_ROLES.CRC) {
        // If user is CRC, check if the CRC role is allowed for editing
        isReadOnly = !surveyData.roles.some((allowedRole) => allowedRole.name === "CRC");
      }

      // Set mode to 'display' if read-only
      if (isReadOnly || isTrialLocked) {
        localSurvey.mode = "display";
      }

      // Set the prepared survey model to state
      setSurvey(localSurvey);
    } catch (error) {
      // Catch and log any errors during the fetch
      console.error("Something went wrong while fetching survey: ", error);
    }
  };

  const confirmDelete = async () => {
    let retries = 2;
    let attempt = 0;
    let success = false;

    logUserAction("Click on Settings");

    while (attempt <= retries && !success) {
      try {
        const response = await axios.post(
          `${__API_URL__}/api/ecrf/save_new_ecrf_answers/`,
          {
            ecrf_model: surveyId,
            answerJson: null,
            visit_number: visit_number,
            trialId: trialId,
            patient_id: patientId,
            sessionId: selectedSessionId,
            possible_incorrect_data: false,
            has_constraint_error: false,
            is_delete: true,
          },
          {
            headers: {
              Authorization: `Token ${token}`,
            },
            timeout: 10000,
          }
        );

        survey.clear();
        setAnswerJson(response?.data?.data?.answerJson);

        queryClient.invalidateQueries({ queryKey: ["visits_status_dropdown"] });
        queryClient.invalidateQueries({ queryKey: ["visit_overview"] });
        queryClient.invalidateQueries({ queryKey: ["ecrf_blocks_per_visit"] });
        queryClient.invalidateQueries({ queryKey: ["ecrf_change_logs"] });
        queryClient.invalidateQueries({ queryKey: ["previous_ecrfs_visits"] });
        success = true;

        logAction.mutate({
          action_type: `Successfully reset the eCRF block "${selectedBlock}" in visit ${visitName}`,
          trial_name: trialName,
          patient_id: patientId,
        });
        toast.success("Block Cleared Successfully.");
      } catch (error) {
        console.error(`Attempt ${attempt + 1} failed:`, error);

        if (attempt >= retries) {
          toast.error("Something went wrong while deleting.");
        }
        attempt += 1;
        await new Promise((res) => setTimeout(res, 1000));
      }
    }

    setShowResetModal(false);
  };

  const saveSurveyAnswer = (
    answerJson,
    possibleIncorrectData,
    hasConstraintsError,
    isFinalSave = true
  ) => {
    const payloadAnswerJson = {
      ...answerJson,
    };

    // While saving convert to ddmmyyy if BRTHDATE field exists
    if ("BRTHDATE" in payloadAnswerJson && payloadAnswerJson.BRTHDATE) {
      payloadAnswerJson.BRTHDATE = convertToDDMMYYYY(payloadAnswerJson.BRTHDATE);
    }

    axios
      .post(
        `${__API_URL__}/api/ecrf/save_new_ecrf_answers/`,
        {
          ecrf_model: surveyId,
          answerJson: payloadAnswerJson,
          visit_number: visit_number,
          trialId: trialId,
          patient_id: patientId,
          sessionId: selectedSessionId,
          possible_incorrect_data: possibleIncorrectData,
          has_constraint_error: hasConstraintsError,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then((res) => {
        if (isFinalSave) {
          survey.clear();
          setAnswerJson(res?.data?.data?.answerJson);
        }
        queryClient.invalidateQueries({ queryKey: ["visits_status_dropdown"] });
        queryClient.invalidateQueries({ queryKey: ["visit_overview"] });
        queryClient.invalidateQueries({ queryKey: ["ecrf_blocks_per_visit"] });
        queryClient.invalidateQueries({ queryKey: ["visit_schedule"] });
        queryClient.invalidateQueries({ queryKey: ["ecrf_change_logs"] });
        queryClient.invalidateQueries({ queryKey: ["previous_ecrfs_visits"] });
        setHasUnsavedEcrfChanges?.(false);

        logAction.mutate({
          action_type: `Successfully saved the eCRF block "${selectedBlock}" in visit "${visitName}"`,
          trial_name: trialName,
          patient_id: patientId,
        });
      })
      .catch((err) => {
        console.error("Error: ", err);
      });
  };

  const getSurveyAnswer = () => {
    axios
      .get(
        `${__API_URL__}/api/ecrf/get_new_ecrf_answers/?ecrfModelId=${surveyId}&visitNumber=${visit_number}&trialId=${trialId}&patientId=${patientId}&sessionId=${selectedSessionId}&`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then((res) => {
        if (res?.data?.data?.answerJson) {
          isShowingErrorsOnly.current = true;
        }

        setAnswerJson(res?.data?.data?.answerJson);
        setStatusObject(res?.data?.data?.status_object);
      })
      .catch((err) => {
        console.error("Error: ", err);
      });
  };

  return (
    <>
      {!fromQueryTable ? (
        photo_session || patientId === undefined ? (
          <>
            <Stack direction={"row"} justifyContent={"flex-end"} alignItems={"center"} gap={2}>
              {role === USER_ROLES.REVIEWER && (
                <>
                  <CustomButton
                    size="small"
                    onClick={() =>
                      window.open(
                        `/patient/${patientId}/photos/${photoSessionId}?visitName=${encodeURIComponent(
                          visitName
                        )}&trial=${trialId}`,
                        "_blank"
                      )
                    }
                  >
                    Visit App Photos
                  </CustomButton>

                  <CustomButton
                    size="small"
                    onClick={() =>
                      window.open(
                        `/patient/${patientId}/video/${photoSessionId}?visitName=${encodeURIComponent(
                          visitName
                        )}&trial=${trialId}`,
                        "_blank"
                      )
                    }
                  >
                    Visit App Videos
                  </CustomButton>

                  <CustomButton
                    size="small"
                    onClick={() =>
                      window.open(
                        `/patient/${patientId}/sessions_external_photos/${photoSessionId}?visitName=${encodeURIComponent(
                          visitName
                        )}&trial=${trialId}`,
                        "_blank"
                      )
                    }
                  >
                    Visit External Photos
                  </CustomButton>

                  <CustomButton
                    size="small"
                    onClick={() =>
                      window.open(
                        `/patient/${patientId}/sessions_external_videos/${photoSessionId}?visitName=${encodeURIComponent(
                          visitName
                        )}&trial=${trialId}`,
                        "_blank"
                      )
                    }
                  >
                    Visit External Videos
                  </CustomButton>
                </>
              )}

              {!isTrialLocked &&
                INV_RELATED_ROLES.includes(role) &&
                allowCopyingFromOtherVisitsEcrfs &&
                visits?.length > 0 && (
                  <FormControl size="small" margin="dense">
                    <InputLabel id="visit-select-label" sx={{ fontSize: "0.85rem" }}>
                      Autofill eCRF From
                    </InputLabel>

                    <Select
                      labelId="visit-select-label"
                      value={selectedVisitOption}
                      label={"Autofill eCRF From"}
                      sx={{
                        minWidth: 220,
                        fontSize: "0.85rem",
                      }}
                      onChange={(e) => {
                        setSelectedVisitOption(e.target.value);
                        setAnswerJson(e.target.value?.survey_answerjson);
                      }}
                    >
                      {visits?.map((visit) => (
                        <MenuItem key={visit.id} value={visit}>
                          {visit.visit_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

              <CustomButton
                size="small"
                data-testid="ecrf-fullscreen-btn"
                onClick={() => setLargeModalShow(true)}
              >
                Full Screen
              </CustomButton>

              {!isTrialLocked && showResetButton && !statusObject?.cro_block_status_bool && (
                <CustomButton
                  size="small"
                  data-testid="reset-block-btn"
                  onClick={() => setShowResetModal(true)}
                  style={{ width: "100px" }}
                >
                  Reset Block
                </CustomButton>
              )}
            </Stack>

            {!largeModalShow ? (
              <Survey model={survey} />
            ) : (
              <Dialog open={largeModalShow} onClose={() => setLargeModalShow(false)} fullScreen>
                <DialogTitle
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span />
                  <Stack direction="row" spacing={1} alignItems="center">
                    {!isTrialLocked && showResetButton && !statusObject?.cro_block_status_bool && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setShowResetModal(true)}
                        sx={{ width: 200 }}
                      >
                        Reset Block
                      </Button>
                    )}
                    <IconButton onClick={() => setLargeModalShow(false)}>
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </DialogTitle>
                <DialogContent>
                  <Survey model={survey} />
                </DialogContent>
              </Dialog>
            )}
          </>
        ) : (
          "Visit Not Started"
        )
      ) : (
        <Dialog
          open={showEcrfBlock}
          onClose={() => setShowEcrfBlock(false)}
          fullScreen={!useCompactModal}
          maxWidth={useCompactModal ? "sm" : undefined}
          fullWidth={useCompactModal}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <IconButton onClick={() => setShowEcrfBlock(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Survey model={survey} />
          </DialogContent>
        </Dialog>
      )}

      {/* Reset confirmation dialog */}
      <Dialog open={showResetModal} onClose={() => setShowResetModal(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="body1">Are you sure you want to reset this block?</Typography>

            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
              <Button variant="outlined" onClick={() => setShowResetModal(false)}>
                No
              </Button>
              <Button variant="contained" color="error" onClick={confirmDelete}>
                Yes
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Incomplete date warning dialog */}
      {show_incomplete_date_warning_on_ecrf && (
        <Dialog open={showDateWarning} onClose={() => setShowDateWarning(false)}>
          <DialogTitle>Incomplete Visit Dates Reminder</DialogTitle>
          <DialogContent>
            <Box>
              <Typography variant="body1">
                Please provide the <strong>In-Clinic Visit Start Date</strong>.
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
                <CustomButton onClick={() => setShowDateWarning(false)}>
                  Okay - I will fill it now
                </CustomButton>
              </Stack>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default EcrfBlocks;
